import type { FieldValidationResultType, PostPaymentOrderError } from '@dtp/membership-service-types';
import { FieldValidationAction } from '@dtp/membership-service-types/dist/form/FieldValidationActionTypes';
import { apiClient, ApiPaths, ApiType } from '@shared/apiClient';

export const MAIN_MEMBER_EMAIL_FIELD = 'CustomerProductsRequests[0].Profile.Email';
export const MEMBERSHIP_RECOGNITION_DEBOUNCE_MS = 4000;

const emailRecognitionActions = [
  FieldValidationAction.LoginCreateMembership,
  FieldValidationAction.LoginMyMembership,
  FieldValidationAction.LoggedInIsMember,
  FieldValidationAction.LoggedInHasUnpaidInvoices,
] as const;

const DEFAULT_EMAIL_RECOGNITION_MESSAGE = 'E-postadressen er allerede registrert hos oss.';

type EmailRecognitionAction = (typeof emailRecognitionActions)[number];

export interface MembershipRecognitionResponse {
  recognized: boolean;
  action?: FieldValidationAction | null;
  message?: string;
}

export type MembershipRecognitionResult =
  | { result: 'notRecognized' }
  | { result: 'validationError'; validationError: PostPaymentOrderError }
  | { result: 'requestError' };

export const normalizeMembershipRecognitionEmail = (email?: string | null): string =>
  (email || '').trim().toLowerCase();

export const isLocallyValidMembershipRecognitionEmail = (email?: string | null): boolean => {
  const normalizedEmail = normalizeMembershipRecognitionEmail(email);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
};

export const isEmailRecognitionAction = (action: unknown): action is EmailRecognitionAction =>
  emailRecognitionActions.includes(action as FieldValidationAction);

export const getEmailRecognitionError = (validationError: PostPaymentOrderError): PostPaymentOrderError | undefined => {
  const emailError = validationError.validationResults.find(
    (error) => error.field === MAIN_MEMBER_EMAIL_FIELD && isEmailRecognitionAction(error.action),
  );

  if (!emailError) {
    return undefined;
  }

  return {
    ...validationError,
    validationResults: [emailError],
  };
};

export const createEmailRecognitionValidationError = (
  recognition: MembershipRecognitionResponse,
): PostPaymentOrderError | undefined => {
  if (!recognition.recognized) {
    return undefined;
  }

  const action = isEmailRecognitionAction(recognition.action) ? recognition.action : null;

  const emailError: FieldValidationResultType = {
    field: MAIN_MEMBER_EMAIL_FIELD,
    message: recognition.message || DEFAULT_EMAIL_RECOGNITION_MESSAGE,
    isValid: false,
    action,
  };

  return {
    isValid: false,
    salesOrder: null,
    validationResults: [emailError],
  } as PostPaymentOrderError;
};

export const validateEmailRecognition = async (
  email: string,
  signal?: AbortSignal,
): Promise<MembershipRecognitionResult> => {
  const normalizedEmail = normalizeMembershipRecognitionEmail(email);

  if (!isLocallyValidMembershipRecognitionEmail(normalizedEmail)) {
    return { result: 'notRecognized' };
  }

  try {
    const response = await apiClient.post<MembershipRecognitionResponse>(ApiType.apimMembershipApi, {
      url: ApiPaths[ApiType.apimMembershipApi].profileMembershipRecognition,
      data: { email: normalizedEmail },
      withCredentials: true,
      signal,
    });
    const validationError = createEmailRecognitionValidationError(response.data);

    if (validationError) {
      return { result: 'validationError', validationError };
    }

    return { result: 'notRecognized' };
  } catch {
    return { result: 'requestError' };
  }
};
